import {
  Checkbox,
  mergeClasses,
  Table,
  TableBody,
  TableCell,
  TableCellLayout,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Tooltip,
} from "@fluentui/react-components";
import { ColumnActionsMenu } from "components/columnActionsMenu/ColumnActionsMenu";
import { SortingDirection } from "models/enums/SortingOrder";
import { ITableColumn } from "models/ITableColumn";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  MIN_COLUMN_WIDTH,
  useAdminColumnStyles,
  useAdminSelectionColumnStyles,
  useAdminTableStyles,
} from "./AdminTable.styles";
import { AdminTableActions } from "./AdminTableActions";
import { IAdminTableProps } from "./AdminTable.types";

export const AdminTable = <T,>(props: IAdminTableProps<T>): JSX.Element => {
  const {
    items,
    allItems,
    columns,
    getRowId,
    rowActions,
    sortColumn,
    isSortDescending,
    onSortChange,
    emptyStateMessage = "No items found",
    actionsWidth,
    actionsHeaderLabel = "Actions",
    highlightedRowId,
    indentFirstColumn = false,
    selectable = false,
    selectedRowIds,
    onSelectionChange,
    getRowLabel,
  } = props;

  const styles = useAdminTableStyles();
  const colStyles = useAdminColumnStyles();
  const selColStyles = useAdminSelectionColumnStyles();
  const hasActions = rowActions && rowActions.length > 0;

  const actionsStyle = useMemo(
    () =>
      actionsWidth
        ? ({ "--actions-width": actionsWidth } as React.CSSProperties)
        : undefined,
    [actionsWidth],
  );

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const columnWidthsRef = useRef<Record<string, number>>({});
  columnWidthsRef.current = columnWidths;

  const resizeRef = useRef<{
    columnId: string;
    startX: number;
    startWidth: number;
  } | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      cleanupRef.current?.();
    };
  }, []);

  const snapshotAllColumns = useCallback(
    (headerCell: HTMLElement): Record<string, number> => {
      const row = headerCell.closest("[role='row']") as HTMLElement | null;
      if (!row) return {};
      const snapshot: Record<string, number> = {};
      const headers = Array.from(row.querySelectorAll("[role='columnheader']"));
      const offset = (hasActions ? 1 : 0) + (selectable ? 1 : 0);
      columns.forEach((col, i) => {
        if (headers[i + offset]) {
          snapshot[col.columnId.toString()] = (
            headers[i + offset] as HTMLElement
          ).offsetWidth;
        }
      });
      return snapshot;
    },
    [columns, hasActions, selectable],
  );

  const handleResizeStart = useCallback(
    (e: React.MouseEvent, columnId: string) => {
      e.preventDefault();
      e.stopPropagation();

      const headerCell = (e.target as HTMLElement).closest(
        "[role='columnheader']",
      ) as HTMLElement | null;
      if (!headerCell) return;

      let currentWidths = columnWidthsRef.current;
      if (Object.keys(currentWidths).length === 0) {
        currentWidths = snapshotAllColumns(headerCell);
        setColumnWidths(currentWidths);
      }

      const startWidth = currentWidths[columnId] || headerCell.offsetWidth;

      resizeRef.current = {
        columnId,
        startX: e.clientX,
        startWidth,
      };

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!resizeRef.current) return;
        const diff = moveEvent.clientX - resizeRef.current.startX;
        const newWidth = Math.max(
          MIN_COLUMN_WIDTH,
          resizeRef.current.startWidth + diff,
        );

        setColumnWidths((prev) => {
          const ref = resizeRef.current;
          if (!ref) return prev;
          return { ...prev, [ref.columnId]: newWidth };
        });
      };

      const handleMouseUp = () => {
        resizeRef.current = null;
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        cleanupRef.current = null;
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      cleanupRef.current = handleMouseUp;
    },
    [snapshotAllColumns],
  );

  const columnStyleMap = useMemo(() => {
    const map: Record<string, React.CSSProperties> = {};
    for (const [columnId, width] of Object.entries(columnWidths)) {
      if (width === undefined) continue;
      map[columnId] = {
        boxSizing: "border-box",
        flexGrow: width,
        flexShrink: 0,
        flexBasis: `${width}px`,
        minWidth: `${MIN_COLUMN_WIDTH}px`,
      };
    }
    return map;
  }, [columnWidths]);

  const tableStyle = useMemo((): React.CSSProperties | undefined => {
    const ids = Object.keys(columnWidths);
    if (ids.length === 0) return undefined;
    const totalColumnsWidth = Object.values(columnWidths).reduce(
      (sum, w) => sum + w,
      0,
    );
    const actionsW = hasActions
      ? parseInt(actionsWidth || "120", 10) || 120
      : 0;
    const selectionW = selectable ? 44 : 0;
    const total = totalColumnsWidth + actionsW + selectionW;
    return { width: `max(100%, ${total}px)` };
  }, [columnWidths, hasActions, actionsWidth, selectable]);

  const selectableIds = useMemo(
    () => (allItems ?? items).map((item) => getRowId(item)),
    [allItems, items, getRowId],
  );

  const selectedCount = useMemo(() => {
    if (!selectedRowIds) return 0;
    return selectableIds.filter((id) => selectedRowIds.has(id)).length;
  }, [selectableIds, selectedRowIds]);

  const allSelected =
    selectableIds.length > 0 && selectedCount === selectableIds.length;
  const someSelected =
    selectedCount > 0 && selectedCount < selectableIds.length;

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (!onSelectionChange) return;
      const next = new Set(selectedRowIds ?? []);
      if (checked) {
        selectableIds.forEach((id) => next.add(id));
      } else {
        selectableIds.forEach((id) => next.delete(id));
      }
      onSelectionChange(next);
    },
    [selectableIds, selectedRowIds, onSelectionChange],
  );

  const handleSelectRow = useCallback(
    (id: string, checked: boolean) => {
      if (!onSelectionChange) return;
      const next = new Set(selectedRowIds ?? []);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      onSelectionChange(next);
    },
    [selectedRowIds, onSelectionChange],
  );

  const handleResizeKeyDown = useCallback(
    (e: React.KeyboardEvent, columnId: string) => {
      const step = e.shiftKey ? 20 : 5;
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        const delta = e.key === "ArrowRight" ? step : -step;

        setColumnWidths((prev) => {
          let currentWidths = prev;
          if (Object.keys(currentWidths).length === 0) {
            const headerCell = (e.target as HTMLElement).closest(
              "[role='columnheader']",
            ) as HTMLElement | null;
            if (!headerCell) return prev;
            currentWidths = snapshotAllColumns(headerCell);
          }
          const currentWidth = currentWidths[columnId] ?? MIN_COLUMN_WIDTH;
          const newWidth = Math.max(MIN_COLUMN_WIDTH, currentWidth + delta);
          return { ...currentWidths, [columnId]: newWidth };
        });
      }
    },
    [snapshotAllColumns],
  );

  const getSortingDirection = (columnId: string): SortingDirection => {
    if (sortColumn !== columnId) {
      return SortingDirection.None;
    }
    return isSortDescending
      ? SortingDirection.Descending
      : SortingDirection.Ascending;
  };

  const renderCellValue = (item: T, column: ITableColumn<T>): JSX.Element => {
    if (column.renderCell) {
      return <>{column.renderCell(item)}</>;
    }
    const value =
      (item as Record<string, unknown>)[column.columnId.toString()] ?? "";
    return <>{String(value)}</>;
  };

  return (
    <div className={styles.tableContainer}>
      <Table noNativeElements className={styles.table} style={tableStyle}>
        <TableHeader className={styles.tableHeader}>
          <TableRow>
            {selectable && (
              <TableHeaderCell
                className={mergeClasses(
                  styles.headerCell,
                  selColStyles.selectionColumn,
                )}
              >
                <Checkbox
                  aria-label="Select all"
                  checked={allSelected ? true : someSelected ? "mixed" : false}
                  onChange={(_e, data) =>
                    handleSelectAll(data.checked === true)
                  }
                />
              </TableHeaderCell>
            )}
            {hasActions && (
              <TableHeaderCell
                className={mergeClasses(
                  styles.headerCell,
                  styles.headerCellResizable,
                  !selectable ? styles.firstColumnCell : undefined,
                  colStyles.actions,
                )}
                style={actionsStyle}
                aria-label="Row actions"
              >
                <TableCellLayout className={styles.headerCellContent}>
                  {actionsHeaderLabel}
                </TableCellLayout>
                <div
                  className={styles.resizeHandle}
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="Actions column separator"
                />
              </TableHeaderCell>
            )}
            {columns.map((column, colIndex) => {
              const isSortable = column.isSortable !== false;
              const columnId = column.columnId.toString();
              const isLast = colIndex === columns.length - 1;

              return (
                <TableHeaderCell
                  key={columnId}
                  className={mergeClasses(
                    styles.headerCell,
                    !isLast ? styles.headerCellResizable : undefined,
                    isSortable ? styles.headerCellSortable : undefined,
                    column.className,
                  )}
                  style={columnStyleMap[columnId]}
                >
                  <TableCellLayout className={styles.headerCellContent}>
                    <div className={styles.headerCellInner}>
                      <Tooltip
                        content={column.displayValue}
                        relationship="label"
                        positioning="above"
                      >
                        <span className={styles.headerCellLabel}>
                          {column.displayValue}
                        </span>
                      </Tooltip>
                      {column.headerEndIcon}
                      {isSortable && onSortChange && (
                        <ColumnActionsMenu
                          columnKey={columnId}
                          displayValue={column.displayValue}
                          isSortable={isSortable}
                          sortingDirection={getSortingDirection(columnId)}
                          onSortClicked={onSortChange}
                        />
                      )}
                    </div>
                  </TableCellLayout>
                  {!isLast && (
                    <div
                      className={styles.resizeHandle}
                      onMouseDown={(e) => handleResizeStart(e, columnId)}
                      onKeyDown={(e) => handleResizeKeyDown(e, columnId)}
                      role="separator"
                      tabIndex={0}
                      aria-orientation="vertical"
                      aria-label={`Resize ${column.displayValue} column`}
                    />
                  )}
                </TableHeaderCell>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length === 0 ? (
            <TableRow>
              <TableCell className={styles.emptyStateCell}>
                {emptyStateMessage}
              </TableCell>
            </TableRow>
          ) : (
            items.map((item) => (
              <TableRow
                key={getRowId(item)}
                className={mergeClasses(
                  styles.tableRow,
                  highlightedRowId === getRowId(item)
                    ? styles.tableRowHighlighted
                    : undefined,
                )}
              >
                {selectable && (
                  <TableCell
                    className={mergeClasses(
                      styles.tableCell,
                      selColStyles.selectionColumn,
                    )}
                  >
                    <Checkbox
                      aria-label={
                        getRowLabel
                          ? `Select ${getRowLabel(item)}`
                          : "Select row"
                      }
                      checked={selectedRowIds?.has(getRowId(item)) ?? false}
                      onChange={(_e, data) =>
                        handleSelectRow(getRowId(item), data.checked === true)
                      }
                    />
                  </TableCell>
                )}
                {hasActions && (
                  <TableCell
                    className={mergeClasses(
                      styles.tableCell,
                      colStyles.actions,
                    )}
                    style={actionsStyle}
                  >
                    <AdminTableActions item={item} actions={rowActions} />
                  </TableCell>
                )}
                {columns.map((col, colIndex) => (
                  <TableCell
                    key={`${getRowId(item)}-${col.columnId}`}
                    className={mergeClasses(
                      styles.tableCell,
                      indentFirstColumn && colIndex === 0 && !selectable
                        ? styles.firstColumnCell
                        : undefined,
                      col.className,
                    )}
                    style={columnStyleMap[col.columnId.toString()]}
                  >
                    <TableCellLayout truncate>
                      {renderCellValue(item, col)}
                    </TableCellLayout>
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};
